import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { clientAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { formatDateMonthDay } from '../../utils/timeFormat';
import { 
  LogOut, Plus, Calendar, Users, AlertCircle, Star, 
  Heart, UserPlus, Ban, Settings, Building2, FileText, MessageSquare, ClipboardCheck, Search, QrCode, Briefcase
} from 'lucide-react';
import MessagesIconButton from '../../components/MessagesIconButton';  // Iter-270
import ClientBriefingAttentionCard from '../../components/client/ClientBriefingAttentionCard';  // Iter-406

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({
    totalGigs: 0,
    activeGigs: 0,
    totalBookings: 0,
    favoritesCount: 0,
    groupsCount: 0,
  });
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractCompliance, setContractCompliance] = useState({ is_compliant: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const [gigsRes, bookingsRes] = await Promise.all([
        clientAPI.getShifts(),
        clientAPI.getBookings(),
      ]);

      setGigs(gigsRes.data);
      
      let favCount = 0;
      let groupCount = 0;
      
      // Fetch favorites count
      try {
        const favRes = await fetch(`${baseUrl}/api/client/favorites`, { headers });
        const favData = await favRes.json();
        favCount = Array.isArray(favData) ? favData.length : 0;
      } catch (e) {
        console.error('Error fetching favorites:', e);
      }
      
      // Fetch groups count
      try {
        const groupsRes = await fetch(`${baseUrl}/api/client/groups`, { headers });
        const groupsData = await groupsRes.json();
        groupCount = Array.isArray(groupsData) ? groupsData.length : 0;
      } catch (e) {
        console.error('Error fetching groups:', e);
      }
      
      setStats({
        totalGigs: gigsRes.data.length,
        activeGigs: gigsRes.data.filter((s) => s.status === 'open').length,
        totalBookings: bookingsRes.data.length,
        favoritesCount: favCount,
        groupsCount: groupCount,
      });

      // Check contract compliance
      try {
        const complianceRes = await fetch(`${baseUrl}/api/contracts/check-compliance`, { headers });
        const compliance = await complianceRes.json();
        setContractCompliance(compliance);
      } catch (e) {
        console.error('Error checking compliance:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-cyan-600 shrink-0">iEvntür</h1>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <MessagesIconButton targetPath="/client/messages-center" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client/profile')}
              className="text-gray-600 hover:text-gray-900 px-2 sm:px-3"
              data-testid="profile-button"
              title="Profile"
            >
              <Building2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Profile</span>
            </Button>
            <span className="text-sm text-gray-600 hidden lg:inline truncate max-w-[200px]">{user?.email}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout} 
              className="border-gray-300 text-gray-700 hover:bg-gray-100 px-2 sm:px-3"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* AI Briefing Assistant — Iter-406 */}
        <ClientBriefingAttentionCard />

        {/* Contract Compliance Warning */}
        {!contractCompliance.is_compliant && (
          <Alert className="mb-6 bg-amber-50 border-amber-300">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              You have {contractCompliance.pending_required_contracts} required contract(s) to accept before posting gigs.
              <Button 
                variant="link" 
                className="text-cyan-600 p-0 ml-2 font-medium"
                onClick={() => navigate('/client/contracts')}
              >
                View Contracts →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="total-gigs-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Gigs</CardTitle>
              <Calendar className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalGigs}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm" data-testid="active-gigs-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Active Gigs</CardTitle>
              <Calendar className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.activeGigs}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm" data-testid="total-bookings-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Bookings</CardTitle>
              <Users className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalBookings}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm" data-testid="favorites-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Favorites</CardTitle>
              <Heart className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.favoritesCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button
              onClick={() => navigate('/client/shifts/create')}
              className="h-16 text-base bg-cyan-500 hover:bg-cyan-600 text-white font-semibold shadow-sm"
              disabled={!contractCompliance.is_compliant}
              data-testid="create-gig-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Gig
            </Button>
            <Button
              onClick={() => navigate('/client/bookings')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="view-bookings-button"
            >
              <Users className="w-5 h-5 mr-2" />
              View Bookings
            </Button>
            <Button
              onClick={() => navigate('/client/favorites')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="manage-favorites-button"
            >
              <Heart className="w-5 h-5 mr-2" />
              Favorites & Groups
            </Button>
            <Button
              onClick={() => navigate('/client/ratings')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="manage-ratings-button"
            >
              <Star className="w-5 h-5 mr-2" />
              Ratings
            </Button>
            <Button
              onClick={() => navigate('/client/invoices')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="view-invoices-button"
            >
              <FileText className="w-5 h-5 mr-2" />
              Invoices
            </Button>
            <Button
              onClick={() => navigate('/chat')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="open-messages-button"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Messages
            </Button>
            <Button
              onClick={() => navigate('/quizzes/manage')}
              variant="outline"
              className="h-16 text-base border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="manage-quizzes-button"
            >
              <ClipboardCheck className="w-5 h-5 mr-2" />
              Quizzes
            </Button>
            <Button
              onClick={() => navigate('/client/discover')}
              className="h-16 text-base bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-sm"
              data-testid="discover-talent-button"
            >
              <Search className="w-5 h-5 mr-2" />
              Discover Talent
            </Button>
            <Button
              onClick={() => navigate('/client/attendance')}
              variant="outline"
              className="h-16 text-base border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="attendance-dashboard-button"
            >
              <QrCode className="w-5 h-5 mr-2" />
              Attendance Dashboard
            </Button>
            <Button
              onClick={() => navigate('/client/booking-management')}
              variant="outline"
              className="h-16 text-base border-green-300 text-green-700 hover:bg-green-50"
              data-testid="booking-management-button"
            >
              <Briefcase className="w-5 h-5 mr-2" />
              Booking Management
            </Button>
            <Button
              onClick={() => navigate('/client/reports')}
              variant="outline"
              className="h-16 text-base border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="event-reports-button"
            >
              <FileText className="w-5 h-5 mr-2" />
              Event Reports
            </Button>
          </div>
        </div>

        {/* Recent Gigs */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Recent Gigs</h2>
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="recent-gigs">
            <CardContent className="p-0">
              {gigs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No gigs yet. Create your first gig to get started!
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {gigs.slice(0, 5).map((gig) => (
                    <div 
                      key={gig.id} 
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer" 
                      onClick={() => navigate(`/client/shifts/${gig.id}`)}
                      data-testid="gig-item"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">{gig.venue_name || gig.role_name || 'Gig'}</h3>
                          <p className="text-sm text-gray-500">
                            {gig.city}, {gig.state} • {formatDateMonthDay(gig.shift_date)}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            gig.status === 'open' 
                              ? 'bg-cyan-100 text-cyan-800 border border-cyan-200' 
                              : gig.status === 'filled'
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-gray-100 text-gray-700 border border-gray-200'
                          }`}>
                            {gig.status}
                          </span>
                          <p className="text-sm text-cyan-600 mt-1 font-medium">
                            ${gig.bill_rate}/hr
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
